'use client';

import { useEffect, ReactNode } from 'react';
import fetchIntercept from 'fetch-intercept';
import { ProgressProvider } from '@bprogress/next/app';
import { getContainer } from '@framework/di/getContainer';
import { useRouter } from '@framework/next/navigation';
import { useSessionContext } from '@users/session/SessionContext';
import registerMessagesExecCallback from '@framework/message/registerMessagesExecCallback';
import downloadFile from '@framework/api/downloadFile';
import { checkSession } from '@users/session/interceptors/fetch/checkSession';
import SentryUserData from '@framework/sentry/SentryUserData';
import { GoogleAnalyticsProvider } from '@framework/browser/analytics/google';
import GlobalErrorContext from '@framework/error-handling/GlobalError/GlobalErrorContext';
import TwoFactorAuthentication from '@users/two-factor-authentication/TwoFactorAuthentication';
import { InitAlerts } from '@dealroadshow/uikit-alert';
import { dataroomGradientColorMapping } from '@/dataroom/ui/common/dataroomGradientColorMapping';
import { DataroomTenant } from '@dataroom/tenant/DataroomTenant';
import TimeZoneContextProvider from '@framework/timezone/TimeZoneContext';
import { TNonce } from '@framework/router/Next/headers/ContentSecurityPolicyHeader';

interface IProps {
  children: ReactNode,
  nonce: TNonce,
  tenant: DataroomTenant,
}

const Bootstrap = ({ children, tenant, nonce = '' }: IProps): ReactNode => {
  const container = getContainer();
  const router = useRouter();
  const { currentUser } = useSessionContext();

  const initAutoDownload = (): void => {
    const searchParams = new URLSearchParams(window.location.search);
    const autoDownload = searchParams.get('auto_download');
    if (autoDownload) {
      downloadFile(autoDownload);
      searchParams.delete('auto_download');
      router.replace(`${ window.location.pathname }${ searchParams.toString() ? `?${ searchParams }` : '' }`);
    }
  };

  useEffect(() => {
    // @ts-ignore
    fetchIntercept.register({ response: checkSession(container) });
    registerMessagesExecCallback(container);
    initAutoDownload();
  }, []);

  return (
    <>
      <InitAlerts />
      <SentryUserData user={ currentUser }>
        <GoogleAnalyticsProvider nonce={ nonce } />
        <GlobalErrorContext>
          <TwoFactorAuthentication>
            <TimeZoneContextProvider>
              <ProgressProvider
                height="4px"
                color={ dataroomGradientColorMapping[tenant] }
                options={ { showSpinner: false } }
                shallowRouting
              >
                { children }
              </ProgressProvider>
            </TimeZoneContextProvider>
          </TwoFactorAuthentication>
        </GlobalErrorContext>
      </SentryUserData>
    </>
  );
};

export default Bootstrap;
